import { useEffect } from 'react'
import { rigFloorMethodologyOptions, PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY } from '../components/common'
import { SelectOptions } from '../types'
import { fetchRigFloorConfig } from '../services/api'
import { SelectOptionsPreset } from '../types/kpis-drilling/types'

interface UsePopulateRigFloorConfigProps {
    setSelectedHydratongEquipment: React.Dispatch<React.SetStateAction<SelectOptions | undefined>>
    setSelectedHydrarackerEquipment: React.Dispatch<React.SetStateAction<SelectOptions | undefined>>
    setSelectedTopDriveEquipment: React.Dispatch<React.SetStateAction<SelectOptions | undefined>>
    setSelectedRrtEquipment: React.Dispatch<React.SetStateAction<SelectOptions | undefined>>
    setSelectedCatwalkEquipment: React.Dispatch<React.SetStateAction<SelectOptions | undefined>>
    setSelectedMethodology: React.Dispatch<React.SetStateAction<SelectOptions>>
    setIsUsingRigFloorInterpolation: React.Dispatch<React.SetStateAction<boolean>>
    setInterpolationFrequency: React.Dispatch<React.SetStateAction<number>>
    setMaxDeltaWithoutInterpolation: React.Dispatch<React.SetStateAction<number>>
    setIsUsingRiserInterpolation: React.Dispatch<React.SetStateAction<boolean>>
    setRiserInterpolationFrequency: React.Dispatch<React.SetStateAction<number>>
    setRiserMaxDeltaWithoutInterpolation: React.Dispatch<React.SetStateAction<number>>
    assetId: string
    assetType: string
    operationType: string
    hydratongEquipments: SelectOptions[]
    hydrarackerEquipments: SelectOptions[]
    topDriveEquipments: SelectOptions[]
    rrtEquipments: SelectOptions[]
    catwalkEquipments: SelectOptions[]
    setEnableRigFloor: React.Dispatch<React.SetStateAction<boolean>>
    setContextState: (state: any) => void
}

export const usePopulateRigFloorConfig = ({
    setSelectedHydratongEquipment,
    setSelectedHydrarackerEquipment,
    setSelectedTopDriveEquipment,
    setSelectedRrtEquipment,
    setSelectedCatwalkEquipment,
    setSelectedMethodology,
    setIsUsingRigFloorInterpolation,
    setInterpolationFrequency,
    setMaxDeltaWithoutInterpolation,
    setIsUsingRiserInterpolation,
    setRiserInterpolationFrequency,
    setRiserMaxDeltaWithoutInterpolation,
    assetId,
    assetType,
    operationType,
    hydratongEquipments,
    hydrarackerEquipments,
    topDriveEquipments,
    rrtEquipments,
    catwalkEquipments,
    setEnableRigFloor,
    setContextState
}: UsePopulateRigFloorConfigProps): void => {
    const findMethodologyByName = (name: string): SelectOptionsPreset | undefined => {
        return rigFloorMethodologyOptions.find(it => it.value === name)
    }

    const findEquipmentByUUID = (uuid: string, equipments: SelectOptions[]): SelectOptions | null => {
        if (!uuid || !equipments || equipments.length === 0) return null

        return equipments.find(equipment => equipment?.value === uuid) || null
    }

    useEffect(() => {
        const loadConfig = async (): Promise<void> => {
            const res = await fetchRigFloorConfig(assetType, assetId, operationType)
            if (res.ok) {
                const data = await res.json()
                const {
                    hydratongEquipmentUUID,
                    hydrarackerEquipmentUUID,
                    topDriveEquipmentUUID,
                    rrtEquipmentUUID,
                    catwalkEquipmentUUID,
                    rigFloorInterpolationFrequency,
                    rigFloorMaxDeltaWithoutInterpolation,
                    rigFloorUseInterpolation,
                    riserInterpolationFrequency,
                    riserMaxDeltaWithoutInterpolation,
                    riserUseInterpolation,
                    rigFloorMethodology
                } = data || {}

                const selectedMetholodogy = findMethodologyByName(rigFloorMethodology)
                setSelectedHydratongEquipment(findEquipmentByUUID(hydratongEquipmentUUID, hydratongEquipments))
                setSelectedHydrarackerEquipment(findEquipmentByUUID(hydrarackerEquipmentUUID, hydrarackerEquipments))
                setSelectedTopDriveEquipment(findEquipmentByUUID(topDriveEquipmentUUID, topDriveEquipments))
                setSelectedRrtEquipment(findEquipmentByUUID(rrtEquipmentUUID, rrtEquipments))
                setSelectedCatwalkEquipment(findEquipmentByUUID(catwalkEquipmentUUID, catwalkEquipments))
                setSelectedMethodology(selectedMetholodogy)
                setIsUsingRigFloorInterpolation(rigFloorUseInterpolation ?? false)
                setInterpolationFrequency(rigFloorInterpolationFrequency ?? 0)
                setMaxDeltaWithoutInterpolation(rigFloorMaxDeltaWithoutInterpolation ?? 0)
                setIsUsingRiserInterpolation(riserUseInterpolation ?? false)
                setRiserInterpolationFrequency(riserInterpolationFrequency ?? 0)
                setRiserMaxDeltaWithoutInterpolation(riserMaxDeltaWithoutInterpolation ?? 0)

                // Enable Rig Floor only if a methodology is selected and updated the context state
                if (selectedMetholodogy) {
                    if (selectedMetholodogy?.value === PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY) {
                        setEnableRigFloor(true)
                    }

                    const contextType = operationType === 'reprocessing' ? 'reprocess' : 'realtime'

                    setContextState(prevState => ({
                        ...prevState,
                        [contextType]: {
                            ...prevState?.[contextType],
                            rigfloor: {
                                ...prevState?.[contextType]?.rigfloor,
                                selectedMethodology: selectedMetholodogy
                            }
                        }
                    }))
                }
            }
        }

        if (typeof setSelectedMethodology === 'function') {
            loadConfig()
        }
    }, [
        assetId,
        hydratongEquipments,
        hydrarackerEquipments,
        topDriveEquipments,
        rrtEquipments,
        catwalkEquipments,
        assetType,
        operationType,
        setSelectedHydratongEquipment,
        setSelectedHydrarackerEquipment,
        setSelectedMethodology,
        setIsUsingRigFloorInterpolation,
        setInterpolationFrequency,
        setMaxDeltaWithoutInterpolation,
        setIsUsingRiserInterpolation,
        setRiserInterpolationFrequency,
        setRiserMaxDeltaWithoutInterpolation,
        setSelectedTopDriveEquipment,
        setSelectedRrtEquipment,
        setSelectedCatwalkEquipment,
        setEnableRigFloor
    ])
}
